import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Car, Wrench, DollarSign, Megaphone, Box, Headphones, ChevronRight, Sparkles, BarChart3, Shield, Clock, ArrowRight, Play, CheckCircle2, Award, Users, Globe, Zap, Brain, Target, TrendingUp } from "lucide-react";

const VERTICALS = [
  { icon: Car, title: "Sales", description: "AI-powered lead scoring, vehicle matching, and intelligent pricing optimization", color: "text-blue-500", bgColor: "bg-blue-500/10", link: "/sales" },
  { icon: Wrench, title: "Service", description: "Automated appointment scheduling, repair tracking, and predictive maintenance", color: "text-emerald-500", bgColor: "bg-emerald-500/10", link: "/service" },
  { icon: DollarSign, title: "F&I", description: "Smart finance calculations, warranty recommendations, and compliance automation", color: "text-amber-500", bgColor: "bg-amber-500/10", link: "/fi" },
  { icon: Megaphone, title: "Digital Marketing", description: "AI-driven campaign optimization, audience targeting, and ROI analytics", color: "text-purple-500", bgColor: "bg-purple-500/10", link: "/marketing" },
  { icon: Box, title: "Parts", description: "Intelligent inventory management, automated reordering, and parts matching", color: "text-orange-500", bgColor: "bg-orange-500/10", link: "/parts" },
  { icon: Headphones, title: "Customer Support", description: "24/7 AI chatbot, intelligent inquiry routing, and satisfaction monitoring", color: "text-teal-500", bgColor: "bg-teal-500/10", link: "/support" }
];

const STATS = [
  { value: "45%", label: "Lead Conversion Increase" },
  { value: "3x", label: "Faster Response Time" },
  { value: "60%", label: "Service Efficiency Gain" },
  { value: "$2.5M", label: "Average Revenue Lift" }
];

const CERTIFICATIONS = [
  { name: "ISO 27001", desc: "Information Security" },
  { name: "SOC 2 Type II", desc: "Security & Privacy" },
  { name: "CMMI Level 3", desc: "Process Maturity" },
  { name: "GDPR", desc: "Data Protection" }
];

const CLIENTS = [
  "AutoNation", "Lithia Motors", "Penske", "Sonic Automotive", 
  "Group 1 Auto", "Asbury Auto", "Larry H. Miller", "Hendrick Auto"
];

const WHY_HALCYON = [
  { icon: Brain, title: "AI-First Architecture", desc: "Purpose-built AI agents that understand automotive retail workflows" },
  { icon: Zap, title: "Rapid Deployment", desc: "Go live in weeks, not months. Pre-trained models for automotive use cases" },
  { icon: Target, title: "Industry Expertise", desc: "Over a decade of experience in automotive retail technology solutions" },
  { icon: TrendingUp, title: "Proven ROI", desc: "Average 300% return on investment within the first year of deployment" }
];

export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-950 text-white overflow-x-hidden">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-md border-b border-slate-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-teal-500 flex items-center justify-center">
                <Car className="w-6 h-6 text-white" />
              </div>
              <span className="font-display text-xl font-bold">HalcyonAuto</span>
            </div>
            <div className="hidden md:flex items-center gap-8">
              <a href="#verticals" className="text-slate-400 hover:text-white transition-colors">Solutions</a>
              <a href="#why-halcyon" className="text-slate-400 hover:text-white transition-colors">Why Us</a>
              <a href="#features" className="text-slate-400 hover:text-white transition-colors">Features</a>
              <a href="#clients" className="text-slate-400 hover:text-white transition-colors">Clients</a>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="ghost" className="text-slate-300 hover:text-white" onClick={() => navigate("/login")} data-testid="nav-login">Sign In</Button>
              <Button className="btn-ai" onClick={() => navigate("/register")} data-testid="nav-register">Get Started</Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-32 pb-24 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-hero-gradient"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-blue-900/20 via-transparent to-transparent"></div>
        
        {/* Animated grid background */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{backgroundImage: 'linear-gradient(rgba(59, 130, 246, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(59, 130, 246, 0.1) 1px, transparent 1px)', backgroundSize: '50px 50px'}}></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto text-center">
          {/* <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-sm mb-8 animate-fade-in">
            <Sparkles className="w-4 h-4" /><span>Powered by GPT-5.2 Technology</span>
          </div> */}
          <h1 className="font-display text-5xl md:text-7xl font-bold mb-6 leading-tight animate-slide-up">
            <span className="text-white">Agentic AI for</span><br />
            <span className="text-gradient">Automotive Retail</span>
          </h1>
          <p className="text-lg md:text-xl text-slate-400 max-w-3xl mx-auto mb-10">
            Transform your dealership with intelligent AI agents for Sales, Service, F&I, Digital Marketing, Parts, and Customer Support. Industry-leading solutions from Halcyon Technologies.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button size="lg" className="btn-ai text-lg px-8 py-6 h-auto group" onClick={() => navigate("/register")} data-testid="hero-cta">
              Start Free Trial <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button size="lg" variant="outline" className="text-lg px-8 py-6 h-auto border-slate-700 text-slate-300 hover:bg-slate-800/50">
              <Play className="w-5 h-5 mr-2" /> Watch Demo
            </Button>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section id="stats" className="py-16 border-y border-slate-800/50 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {STATS.map((stat, idx) => (
              <div key={idx} className="text-center" data-testid={`stat-${idx}`}>
                <div className="text-4xl md:text-5xl font-display font-bold text-gradient mb-2">{stat.value}</div>
                <div className="text-slate-400 text-sm md:text-base">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Verticals */}
      <section id="verticals" className="py-24 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-4">AI Agents for Every Dealership Vertical</h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">Specialized AI solutions that understand each department's unique challenges and workflows</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {VERTICALS.map((v, idx) => (
              <div 
                key={idx} 
                className="group p-6 rounded-xl bg-slate-900/50 border border-slate-800 hover:border-slate-700 transition-all hover:-translate-y-1 cursor-pointer" 
                data-testid={`vertical-${v.title.toLowerCase()}`}
                onClick={() => navigate(v.link)}
              >
                <div className={`w-12 h-12 rounded-lg ${v.bgColor} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <v.icon className={`w-6 h-6 ${v.color}`} />
                </div>
                <h3 className="font-display text-xl font-semibold text-white mb-2">{v.title}</h3>
                <p className="text-slate-400 text-sm">{v.description}</p>
                <div className="mt-4 flex items-center text-blue-400 text-sm font-medium group-hover:text-blue-300">
                  Explore <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why HalcyonAuto */}
      <section id="why-halcyon" className="py-24 px-4 bg-slate-900/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-4">Why Choose HalcyonAuto?</h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">Built by Halcyon Technologies - leaders in automotive retail solutions for over a decade</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {WHY_HALCYON.map((item, idx) => (
              <div key={idx} className="p-6 rounded-xl bg-slate-900/50 border border-slate-800 hover:border-blue-500/30 transition-all">
                <div className="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6 text-blue-400" />
                </div>
                <h3 className="font-display text-lg font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-slate-400 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-24 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-6">Enterprise-Grade AI Platform</h2>
              <p className="text-slate-400 text-lg mb-8">Built for automotive retailers who demand reliability, security, and measurable results.</p>
              <div className="space-y-6">
                {[
                  { icon: Sparkles, title: "AI-Powered Agents", desc: "Specialized AI for each dealership vertical, trained on millions of automotive interactions" },
                  { icon: BarChart3, title: "Real-time Analytics", desc: "Live dashboards, performance insights, and AI-driven recommendations" },
                  { icon: Shield, title: "Enterprise Security", desc: "SOC 2 Type II compliant, encrypted at rest and in transit, GDPR ready" },
                  { icon: Clock, title: "24/7 Availability", desc: "AI assistants that never sleep, ensuring customers always get instant responses" }
                ].map((f, idx) => (
                  <div key={idx} className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                      <f.icon className="w-5 h-5 text-blue-400" />
                    </div>
                    <div><h4 className="font-semibold text-white mb-1">{f.title}</h4><p className="text-slate-400 text-sm">{f.desc}</p></div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-teal-500/20 rounded-2xl blur-3xl"></div>
              <div className="relative bg-slate-900 border border-slate-800 rounded-2xl p-6 overflow-hidden">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <span className="ml-2 text-xs text-slate-500">HalcyonAuto Sales Agent</span>
                </div>
                <div className="space-y-4">
                  <div className="ai-bubble-assistant"><p className="text-sm">Hello! I'm your AI Sales Agent. How can I assist you today?</p></div>
                  <div className="ai-bubble-user"><p className="text-sm">I need SUVs under $45k with good fuel efficiency</p></div>
                  <div className="ai-bubble-assistant">
                    <p className="text-sm">Based on your criteria, I found 3 excellent matches:</p>
                    <ul className="text-sm mt-2 space-y-1 text-slate-300">
                      <li>• 2024 Toyota RAV4 Hybrid - $42,500</li>
                      <li>• 2024 Honda CR-V Hybrid - $41,200</li>
                      <li>• 2024 Ford Escape Hybrid - $39,800</li>
                    </ul>
                    <p className="text-sm mt-2">Shall I schedule test drives for any of these?</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Certifications & Trust */}
      <section className="py-16 px-4 bg-slate-900/50 border-y border-slate-800/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h3 className="font-display text-xl font-semibold text-white mb-2">Enterprise Trust & Compliance</h3>
            <p className="text-slate-400 text-sm">Security and compliance are our top priorities</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {CERTIFICATIONS.map((cert, idx) => (
              <div key={idx} className="text-center p-6 rounded-xl bg-slate-900/50 border border-slate-800">
                <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center mx-auto mb-3">
                  <Award className="w-6 h-6 text-blue-400" />
                </div>
                <div className="font-semibold text-white">{cert.name}</div>
                <div className="text-slate-400 text-sm">{cert.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Clients */}
      <section id="clients" className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h3 className="font-display text-xl font-semibold text-white mb-2">Trusted by Leading Dealership Groups</h3>
            <p className="text-slate-400 text-sm">Join hundreds of automotive retailers transforming their operations with AI</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {CLIENTS.map((client, idx) => (
              <div key={idx} className="flex items-center justify-center p-6 rounded-xl bg-slate-900/30 border border-slate-800/50 hover:border-slate-700 transition-colors">
                <span className="font-display font-semibold text-slate-400 hover:text-white transition-colors">{client}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-teal-500/20 rounded-3xl blur-3xl"></div>
            <div className="relative bg-slate-900/80 border border-slate-800 rounded-3xl p-12">
              <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-6">Ready to Transform Your Dealership?</h2>
              <p className="text-slate-400 text-lg mb-10">Join hundreds of automotive retailers using HalcyonAuto to drive growth and efficiency.</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button size="lg" className="btn-ai text-lg px-8 py-6 h-auto" onClick={() => navigate("/register")} data-testid="cta-btn">Start Your Free Trial</Button>
                <div className="flex items-center gap-2 text-slate-400 text-sm"><CheckCircle2 className="w-4 h-4 text-green-500" /><span>No credit card required</span></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 border-t border-slate-800/50">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-teal-500 flex items-center justify-center"><Car className="w-5 h-5 text-white" /></div>
                <span className="font-display font-bold text-white">HalcyonAuto</span>
              </div>
              <p className="text-slate-500 text-sm">Agentic AI solutions for automotive retail. A product of Halcyon Technologies.</p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Solutions</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><a href="#" className="hover:text-white transition-colors">Sales AI</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Service AI</a></li>
                <li><a href="#" className="hover:text-white transition-colors">F&I AI</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Marketing AI</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><a href="https://halcyontek.com" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">About Halcyon</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Careers</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Legal</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><a href="#" className="hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Terms of Service</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Security</a></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-slate-800/50 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-slate-500 text-sm">© 2025 HalcyonAuto. Powered by Halcyon Technologies.</p>
            <div className="flex items-center gap-4 text-slate-500 text-sm">
              <span>Microsoft Gold Partner</span>
              <span>•</span>
              <span>Google Cloud Partner</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
